<script setup>
import password from '@/routes/password'
import { Head, useForm } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  status: {
    type: String,
    default: null,
  },
  email: {
    type: String,
    default: '',
    required: false,
  },
})

const form = useForm({
  email: props.email || '',
})

const toast = useToast()

const onFormSubmit = () => {
  form.post(password.email().url, {
    preserveScroll: true,
    onError: (errors) => {
      if (Object.keys(errors).length === 0) {
        toast.add({
          severity: 'error',
          summary: t('error.summary'),
          detail: t('error.detail'),
          life: 3000,
        })
      }
      form.reset()
    },
  })
}
</script>

<template>
  <Head :title="t('title')" />

  <AuthCard>
    <template #title>{{ t('title') }}</template>
    <template #content>
      <div class="flex flex-col gap-4">
        <p>{{ t('instructions') }}</p>

        <Message v-if="status" severity="info">
          {{ t('status.' + status) }}
        </Message>

        <form v-else class="auth-form" @submit.prevent="onFormSubmit">
          <div class="flex flex-col gap-0.5">
            <IconField>
              <InputIcon class="pi pi-envelope" />
              <InputText
                v-model="form.email"
                name="email"
                type="email"
                :placeholder="t('form.email.placeholder')"
                :invalid="!!form.errors.email"
                required
                fluid />
            </IconField>
            <div class="h-5">
              <Message
                v-if="form.errors.email"
                severity="error"
                size="small"
                variant="simple"
                class="auth-form-error-message">
                {{ form.errors.email }}
              </Message>
            </div>
          </div>

          <div class="mt-4 flex justify-end">
            <Button type="submit" :label="t('form.submit.label')" :loading="form.processing" />
          </div>
        </form>
      </div>
    </template>
  </AuthCard>
</template>

<i18n>
{
  "en": {
    "title": "Forgot Password",
    "instructions": "Please enter your email address to receive a password reset link.",
    "form": {
      "email": {
        "label": "Email",
        "placeholder": "Your email address",
        "error": {
          "required": "Email is required",
          "invalid": "Invalid email address"
        }
      },
      "submit": {
        "label": "Send Password Reset Link"
      }
    },
    "status": {
      "passwords": {
        "sent": "If you have an account, we have sent you an email with a link to reset your password.",
        "throttled": "Please wait before trying again."
      }
    },
    "error": {
      "summary": "Request failed",
      "detail": "The password reset link could not be requested right now. Please try again."
    }
  },
  "de": {
    "title": "Passwort vergessen",
    "instructions": "Bitte gib deine E-Mail-Adresse ein, um einen Link zum Zurücksetzen deines Passworts zu erhalten.",
    "form": {
      "email": {
        "label": "E-Mail",
        "placeholder": "Deine E-Mail-Adresse",
        "error": {
          "required": "E-Mail ist erforderlich",
          "invalid": "Ungültige E-Mail-Adresse"
        }
      },
      "submit": {
        "label": "Passwort zurücksetzen"
      }
    },
    "status": {
      "passwords": {
        "sent": "Falls du ein Konto hast, haben wir dir eine E-Mail mit einem Link zum Zurücksetzen deines Passworts gesendet.",
        "throttled": "Bitte warte, bevor du es erneut versuchst."
      }
    },
    "error": {
      "summary": "Anfrage fehlgeschlagen",
      "detail": "Der Link zum Zurücksetzen deines Passworts konnte gerade nicht angefordert werden. Bitte versuche es erneut."
    }
  }
}
</i18n>
